import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useState } from 'react';
import { FiAlertCircle, FiClock, FiGlobe, FiRepeat, FiCheckCircle, FiXCircle, FiCode } from 'react-icons/fi';
import { BsBuildingFill } from "react-icons/bs";
import { useEffect } from 'react';
import { useAuth } from "@/context/AuthContext";
import { TruncatedUrl } from "@/components/ui/truncated-url";
import { cn } from "@/lib/utils";

const PreviewStep = ({ stepKey, updateStepData, getStepData }) => {
    const actionStepData = getStepData('actionType');
    const configStepData = getStepData('configuration');
    const scheduleStepData = getStepData('schedule');
    const { axiosInstance } = useAuth();

    const allStepsData = {
        actionType: actionStepData,
        configuration: {
            ...configStepData,
            url: actionStepData === 'ping' 
                ? `${configStepData.protocol || 'https:'}//${configStepData.hostname}`
                : configStepData.url
        },
        schedule: { interval: scheduleStepData.interval }
    };

    const [testStatus, setTestStatus] = useState(null);
    const [testResults, setTestResults] = useState(null);
    const [loading, setLoading] = useState(false);
    
    const handleTestRun = async () => {
        setLoading(true);
        setTestStatus('Running...');
        setTestResults(null);

        try {
            let monitorData;
            
            if (actionStepData === 'ping') {
                const [hostname, ...pathParts] = configStepData.hostname.split('/');
                const path = pathParts.length > 0 ? pathParts.join('/') : '';
                
                monitorData = {
                    actionType: actionStepData,
                    settings: {
                        method: configStepData.method || 'GET',
                        protocol: configStepData.protocol || 'https:',
                        hostname: hostname,
                        path: path,
                        requestHeaders: configStepData.requestHeaders,
                        requestBody: configStepData.requestBody,
                        validateSSL: configStepData.validateSSL || false
                    },
                    name: `Test ${actionStepData} Monitor`,
                    interval: scheduleStepData.interval || 60
                };
            } else if (actionStepData === 'browser_agent') {
                monitorData = {
                    actionType: actionStepData,
                    settings: {
                        url: configStepData.url,
                        prompt: configStepData.prompt
                    },
                    name: `Test ${actionStepData} Monitor`,
                    interval: scheduleStepData.interval || 60
                };
            }

            const response = await axiosInstance.post('/monitors/test', monitorData);

            // Determine success based on various possible response formats
            let isSuccess = false;
            let responseTime = null;
            let message = '';
            let details = {};

            // Check for Python service response format
            if (response.data.status) {
                isSuccess = response.data.success;
                responseTime = response.data.responseTime;
                message = response.data.message || '';
                details = response.data;
            } 
            // Check for ping_action result format
            else if (response.data.url) {
                // Check if there's an error field - if so, it's a failure
                if (response.data.error) {
                    isSuccess = false;
                    responseTime = response.data.responseTime;
                    message = response.data.error;
                    details = response.data;
                } else {
                    isSuccess = true;
                    responseTime = response.data.responseTime;
                    message = 'Monitor test completed successfully';
                    details = response.data;
                }
            }
            // Fallback for backward compatibility
            else if (response.data.success !== undefined) {
                isSuccess = response.data.success;
                responseTime = response.data.responseTime;
                message = response.data.message || '';
                details = response.data;
            }
            // If we can't determine success, assume failure
            else {
                isSuccess = false;
                message = 'Unknown response format';
                details = response.data;
            }

            setTestStatus(isSuccess ? 'Success' : 'Error');
            setTestResults({
                responseTime,
                status: isSuccess ? 'Success' : 'Failed',
                message,
                details
            });
        } catch (error) {
            console.error('Error testing monitor:', error);
            setTestStatus('Error');
            setTestResults({
                status: 'Failed',
                message: error.response?.data?.message || 'Failed to test monitor configuration',
                details: error.response?.data?.details || error.response?.data
            });
        } finally {
            setLoading(false);
        }
    };

    // On render, mark the step as complete
    useEffect(() => {
        updateStepData(stepKey, { isComplete: true });
    }, []);


    return (
        <div className="flex flex-col space-y-8 items-center">
            <div className="flex flex-col items-center space-y-3">
                <h1 className="font-semibold text-3xl text-center">Review Monitor</h1>
                <p className="text-center text-muted-foreground">Review & validate your monitor configuration</p>
            </div>
            <div className="w-full max-w-2xl mx-auto space-y-6">
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-8">
                        <CardTitle className="text-2xl font-bold">Monitor Summary</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid gap-8">
                            <div className="flex items-center space-x-4">
                                <BsBuildingFill className="h-6 w-6 text-muted-foreground" />
                                <div className="flex-1 space-y-1">
                                    <p className="text-sm font-medium leading-none">Organization</p>
                                    <p className="text-sm text-muted-foreground">
                                        {allStepsData.configuration.organizationName || 'No organization selected'}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-4">
                                <FiGlobe className="h-6 w-6 text-muted-foreground" />
                                <div className="flex-1 space-y-1">
                                    <p className="text-sm font-medium leading-none">URL</p>
                                    <TruncatedUrl 
                                        url={allStepsData.configuration.url}
                                        maxLength={80}
                                        className="text-sm text-muted-foreground"
                                    />
                                </div>
                                <div className="flex items-center space-x-2">
                                    {actionStepData === 'ping' && (
                                        <Badge variant="secondary">{configStepData.method || 'GET'}</Badge>
                                    )}
                                    {allStepsData?.actionType && (
                                        <Badge variant="secondary" className="uppercase">{allStepsData.actionType.replace('_', ' ')}</Badge>
                                    )}
                                </div>
                            </div>
                            {actionStepData === 'browser_agent' && (
                                <div className="flex items-center space-x-4">
                                    <FiCode className="h-6 w-6 text-muted-foreground" />
                                    <div className="flex-1 space-y-1">
                                        <p className="text-sm font-medium leading-none">Monitoring Instructions</p>
                                        <p className="text-sm text-muted-foreground">{configStepData.prompt}</p>
                                    </div>
                                </div>
                            )}
                            <div className="flex items-center space-x-4">
                                <FiClock className="h-6 w-6 text-muted-foreground" />
                                <div className="flex-1 space-y-1">
                                    <p className="text-sm font-medium leading-none">Check Interval</p>
                                    <p className="text-sm text-muted-foreground">Every {allStepsData.schedule.interval} minutes</p>
                                </div>
                            </div>
                            {actionStepData === 'ping' && configStepData.validateSSL && (
                                <div className="flex items-center space-x-4">
                                    <FiCheckCircle className="h-6 w-6 text-muted-foreground" />
                                    <div className="flex-1 space-y-1">
                                        <p className="text-sm font-medium leading-none">SSL Validation</p>
                                        <p className="text-sm text-muted-foreground">Enabled - Will fail on invalid certificates</p>
                                    </div>
                                </div>
                            )}
                            {actionStepData === 'ping' && (
                                <>
                                    <Separator />
                                    <div>
                                        <h3 className="text-lg font-semibold mb-2">Additional Details</h3>
                                        {configStepData.requestHeaders && (
                                            <div className="mt-4">
                                                <label>Request Headers:</label>
                                                <pre className="mt-2 whitespace-pre-wrap text-xs border rounded-lg p-4">{configStepData.requestHeaders}</pre>
                                            </div>
                                        )}
                                        {configStepData.requestBody && (
                                            <div className="mt-4">
                                                <label>Request Body:</label>
                                                <pre className="mt-2 whitespace-pre-wrap text-xs border rounded-lg p-4">{configStepData.requestBody}</pre>
                                            </div>
                                        )}
                                        {!configStepData.requestHeaders && !configStepData.requestBody && (
                                            <div className="mt-4">
                                                <label>No additional details provided.</label>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                            <Separator />
                            <div>
                                <h3 className="text-lg font-semibold mb-4">Validate your configuration</h3>
                                <div className="space-y-4">
                                    <Button 
                                        variant="outline" 
                                        onClick={handleTestRun} 
                                        disabled={loading}
                                        size="sm"
                                        className="flex items-center"
                                    >
                                        {loading ? (
                                            <>
                                                <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2" />
                                                <span>Testing Monitor Configuration...</span>
                                            </>
                                        ) : (
                                            <>
                                                <FiRepeat className="mr-2 h-4 w-4" />
                                                <span>Run Monitor Test</span>
                                            </>
                                        )}
                                    </Button>
                                    
                                    {testStatus && !loading && (
                                        <div className="mt-4 space-y-4">
                                            <div className="flex items-center space-x-2 text-sm">
                                                {testStatus === 'Success' ? (
                                                    <>
                                                        <FiCheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                                                        <span className="text-green-700 dark:text-green-300">Test passed</span>
                                                        {testResults?.responseTime && (
                                                            <>
                                                                <span className="text-muted-foreground">·</span>
                                                                <span className="text-muted-foreground">{testResults.responseTime} ms</span>
                                                            </>
                                                        )}
                                                    </>
                                                ) : testStatus === 'Error' ? (
                                                    <>
                                                        <FiXCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                                                        <span className="text-red-700 dark:text-red-300">Test failed</span>
                                                    </>
                                                ) : null}
                                            </div>
                                            {testResults && (
                                                <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
                                                    <div className="p-4 space-y-4">
                                                        {/* Action Type Specific Details */}
                                                        {actionStepData === 'browser_agent' && (
                                                            <>
                                                                <div className="flex items-center justify-between">
                                                                    <span className="text-sm text-muted-foreground">Action Type</span>
                                                                    <Badge variant="outline" className="capitalize bg-violet-950/50 text-violet-400">
                                                                        {actionStepData.replace('_', ' ')}
                                                                    </Badge>
                                                                </div>
                                                                <div className="flex items-center justify-between">
                                                                    <span className="text-sm text-muted-foreground">Target URL</span>
                                                                    <TruncatedUrl 
                                                                        url={allStepsData.configuration.url}
                                                                        maxLength={40}
                                                                        className="text-xs font-mono text-violet-400"
                                                                    />
                                                                </div>
                                                                <div className="space-y-2">
                                                                    <span className="text-sm text-muted-foreground">Instructions</span>
                                                                    <div className="rounded-md bg-muted/50 p-3">
                                                                        <p className="text-sm text-muted-foreground">{testResults.details?.settings?.prompt || configStepData.prompt}</p>
                                                                    </div>
                                                                </div>
                                                            </>
                                                        )}
                                                        {actionStepData === 'ping' && (
                                                            <>
                                                                <div className="flex items-center justify-between">
                                                                    <span className="text-sm text-muted-foreground">Method</span>
                                                                    <Badge variant="outline" className="bg-violet-950/50 text-violet-400">
                                                                        {testResults.details?.details?.settings?.method || 'GET'}
                                                                    </Badge>
                                                                </div>
                                                                <div className="flex items-center justify-between">
                                                                    <span className="text-sm text-muted-foreground">URL</span>
                                                                    <TruncatedUrl 
                                                                        url={allStepsData.configuration.url}
                                                                        maxLength={40}
                                                                        className="text-sm font-mono text-violet-400"
                                                                    />
                                                                </div>
                                                                {testResults.details?.details?.statusCode && (
                                                                    <div className="flex items-center justify-between">
                                                                        <span className="text-sm text-muted-foreground">Status Code</span>
                                                                        <Badge variant={testResults.details.details.statusCode >= 400 ? "destructive" : "success"}>
                                                                            {testResults.details.details.statusCode}
                                                                        </Badge>
                                                                    </div>
                                                                )}
                                                                {configStepData.validateSSL && (
                                                                    <div className="flex items-center justify-between">
                                                                        <span className="text-sm text-muted-foreground">SSL Validation</span>
                                                                        <Badge variant="outline" className={cn(
                                                                            (testResults.details?.sslError || testResults.details?.details?.sslError)
                                                                                ? "bg-red-950/50 text-red-400 border-red-800/50"
                                                                                : "bg-green-950/50 text-green-400 border-green-800/50"
                                                                        )}>
                                                                            {(testResults.details?.sslError || testResults.details?.details?.sslError) ? "Failed" : "Enabled & Validated"}
                                                                        </Badge>
                                                                    </div>
                                                                )}
                                                                {testResults.details?.details?.responseSize && (
                                                                    <div className="flex items-center justify-between">
                                                                        <span className="text-sm text-muted-foreground">Response Size</span>
                                                                        <span className="text-sm font-mono">
                                                                            {(testResults.details.details.responseSize / 1024).toFixed(2)} KB
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}

                                                        {/* Common Details */}
                                                        <div className="pt-3 border-t">
                                                            <div className="flex items-center justify-between mb-3">
                                                                <span className="text-sm text-muted-foreground">Result</span>
                                                                <Badge variant="outline" className={cn(
                                                                    testResults.status === 'Success'
                                                                        ? "bg-green-950/50 text-green-400"
                                                                        : "bg-red-950/50 text-red-400"
                                                                )}>
                                                                    {testResults.status}
                                                                </Badge>
                                                            </div>
                                                            <div className={cn(
                                                                "rounded-md p-4",
                                                                testResults.status === 'Success' 
                                                                    ? "bg-violet-950/50 border border-violet-800/50" 
                                                                    : "bg-red-950/50 border border-red-800/50"
                                                            )}>
                                                                <p className={cn(
                                                                    "text-sm",
                                                                    testResults.status === 'Success'
                                                                        ? "text-violet-400"
                                                                        : "text-red-400"
                                                                )}>
                                                                    {testResults.message}
                                                                </p>
                                                            </div>
                                                        </div>

                                                        {/* SSL Certificate Details (if available) */}
                                                        {actionStepData === 'ping' && testResults.details?.details?.sslInfo && (
                                                            <div className="pt-3 border-t">
                                                                <div className="flex items-center gap-3 mb-3">
                                                                    <span className="text-sm font-medium">SSL Certificate</span>
                                                                    <Badge variant="secondary" className="bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300 text-xs">
                                                                        Validated
                                                                    </Badge>
                                                                </div>
                                                                <div className="rounded-md border bg-muted/30 p-4 space-y-3">
                                                                    {testResults.details.details.sslInfo.subject && (
                                                                        <div className="grid grid-cols-3 gap-2">
                                                                            <span className="text-xs text-muted-foreground">Subject:</span>
                                                                            <span className="text-xs font-mono col-span-2">
                                                                                {testResults.details.details.sslInfo.subject.commonName || 
                                                                                 testResults.details.details.sslInfo.subject.CN || 'N/A'}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    {testResults.details.details.sslInfo.issuer && (
                                                                        <div className="grid grid-cols-3 gap-2">
                                                                            <span className="text-xs text-muted-foreground">Issuer:</span>
                                                                            <span className="text-xs font-mono col-span-2">
                                                                                {testResults.details.details.sslInfo.issuer.organizationName || 
                                                                                 testResults.details.details.sslInfo.issuer.O || 'N/A'}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    {testResults.details.details.sslInfo.notBefore && (
                                                                        <div className="grid grid-cols-3 gap-2">
                                                                            <span className="text-xs text-muted-foreground">Valid From:</span>
                                                                            <span className="text-xs col-span-2">{testResults.details.details.sslInfo.notBefore}</span>
                                                                        </div>
                                                                    )}
                                                                    {testResults.details.details.sslInfo.notAfter && (
                                                                        <div className="grid grid-cols-3 gap-2">
                                                                            <span className="text-xs text-muted-foreground">Valid Until:</span>
                                                                            <span className="text-xs col-span-2">{testResults.details.details.sslInfo.notAfter}</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Response Data (if available) */}
                                                        {testResults.details?.details?.data && (
                                                            <div className="pt-3 border-t">
                                                                <div className="flex items-center justify-between mb-3">
                                                                    <span className="text-sm text-muted-foreground">Response Data</span>
                                                                    <div className="flex items-center space-x-2">
                                                                        {testResults.details.details.headers?.['content-type'] && (
                                                                            <Badge variant="outline" className="text-xs bg-neutral-800 text-neutral-400">
                                                                                {testResults.details.details.headers['content-type'].split(';')[0]}
                                                                            </Badge>
                                                                        )}
                                                                        {testResults.details.details.responseSize > 0 && (
                                                                            <Badge variant="secondary" className="text-xs bg-neutral-800 text-neutral-400">
                                                                                {testResults.details.details.responseSize < 1024 
                                                                                    ? `${testResults.details.details.responseSize} B`
                                                                                    : testResults.details.details.responseSize < 1024 * 1024
                                                                                    ? `${(testResults.details.details.responseSize / 1024).toFixed(1)} KB`
                                                                                    : `${(testResults.details.details.responseSize / (1024 * 1024)).toFixed(1)} MB`}
                                                                            </Badge>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className="relative">
                                                                    <div className="max-h-[200px] overflow-y-auto rounded-md border bg-muted/50 p-3">
                                                                        <pre className="text-xs font-mono whitespace-pre-wrap break-all text-muted-foreground">
                                                                            {typeof testResults.details.details.data === 'string'
                                                                                ? testResults.details.details.data
                                                                                : JSON.stringify(testResults.details.details.data, null, 2)
                                                                            }
                                                                        </pre>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <CardTitle className="text-xl">What's Next?</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ul className="space-y-4">
                            <li className="flex items-center space-x-3">
                                <FiRepeat className="h-5 w-5 text-green-500" />
                                <span>Your monitor will start running immediately after creation.</span>
                            </li>
                            <li className="flex items-center space-x-3">
                                <FiAlertCircle className="h-5 w-5 text-yellow-500" />
                                <span>You'll receive alerts if any issues are detected.</span>
                            </li>
                        </ul>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default PreviewStep;
